import { Injectable, inject } from '@angular/core';
import { Firestore, doc, runTransaction } from '@angular/fire/firestore';

export interface ProjectIdResult {
  projectId: string;
  departmentCode: string;
  sequenceNumber: number;
}

/**
 * Generates sequential, department-based project IDs (e.g. W4, WR2, RD1).
 * Counters are stored as department-prefixed keys on the existing
 * counters/projectIds document so no new Firestore document needs to be
 * created (operators only need update permission, not create permission).
 */
@Injectable({ providedIn: 'root' })
export class ProjectIdService {
  private firestore = inject(Firestore);

  private readonly DEPT_PREFIX_MAP: Record<string, string> = {
    'works':                               'W',
    'pwd':                                 'W',
    'water resources':                     'WR',
    'rural development':                   'RD',
    'panchayati raj & drinking water':     'PR',
    'panchayati raj':                      'PR',
    'housing & urban development':         'HUD',
    'housing':                             'HUD',
    'health & family welfare':             'H',
    'health':                              'H',
    'tourism':                             'T',
    'sports & youth services':             'SY',
    'sports':                              'SY',
    'higher education':                    'HE',
    'schools and mass':                    'SM',
    'planning & convergence':             'PC',
    'odia language, literature & culture': 'OL',
    'odia language':                       'OL',
  };

  getDeptPrefix(deptName: string): string {
    const key = (deptName || '').toLowerCase().trim();
    if (this.DEPT_PREFIX_MAP[key]) return this.DEPT_PREFIX_MAP[key];
    // Partial / substring match — find the longest matching key
    let best = '';
    let bestPrefix = 'P';
    for (const [mapKey, prefix] of Object.entries(this.DEPT_PREFIX_MAP)) {
      if ((key.includes(mapKey) || mapKey.includes(key)) && mapKey.length > best.length) {
        best = mapKey;
        bestPrefix = prefix;
      }
    }
    return bestPrefix;
  }

  /**
   * Reserve the next department-scoped project ID atomically.
   *
   * Uses the existing `counters/projectIds` document (operators already have
   * update rights on it).  Department counts are stored as new keys alongside
   * the legacy `year`/`seq` fields, e.g. { W: 4, WR: 2, year: 2026, seq: 12 }.
   */
  async reserveNextProjectId(deptName?: string): Promise<ProjectIdResult> {
    const departmentCode = this.getDeptPrefix(deptName || '');
    const counterRef = doc(this.firestore, 'counters', 'projectIds');

    const sequenceNumber = await runTransaction(this.firestore, async (tx) => {
      const snap = await tx.get(counterRef);
      const data = snap.exists() ? snap.data() : {};
      const currentSeq = Number(data[departmentCode]) || 0;
      const nextSeq = currentSeq + 1;
      tx.set(
        counterRef,
        { [departmentCode]: nextSeq, updatedAt: new Date().toISOString() },
        { merge: true }
      );
      return nextSeq;
    });

    return { projectId: `${departmentCode}${sequenceNumber}`, departmentCode, sequenceNumber };
  }
}
